import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileSignature, FileCheck, Clock, AlertCircle, BarChart3 } from "lucide-react";
import { ContractsTable } from "@/components/contracts/ContractsTable";
import { AddContractDialog } from "@/components/contracts/AddContractDialog";
import { GenerateContractDialog } from "@/components/contracts/GenerateContractDialog";
import { ContractTemplateManager } from "@/components/contracts/ContractTemplateManager";
import { ContractAnalyticsDashboard } from "@/components/contracts/ContractAnalyticsDashboard";
import { useContracts } from "@/hooks/useContracts";
import { useRealtimeContracts } from "@/hooks/useRealtimeContracts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Contracts() {
  const { data: contracts } = useContracts();
  
  // Enable real-time notifications for contracts
  useRealtimeContracts();

  const stats = {
    total: contracts?.length || 0,
    signed: contracts?.filter(c => c.status === "signed").length || 0,
    pending: contracts?.filter(c => ["sent", "viewed"].includes(c.status)).length || 0,
    draft: contracts?.filter(c => c.status === "draft").length || 0,
  };

  return (
    <div className="space-y-6" data-wizard="contracts">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Contracts</h1>
          <p className="text-muted-foreground">
            Manage contracts and e-signature workflows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GenerateContractDialog />
          <AddContractDialog />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Contracts</CardTitle>
            <FileSignature className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Signed</CardTitle>
            <FileCheck className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{stats.signed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending Signature</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Drafts</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="contracts" className="w-full">
        <TabsList>
          <TabsTrigger value="contracts">All Contracts</TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="contracts">
          <Card>
            <CardHeader>
              <CardTitle>All Contracts</CardTitle>
            </CardHeader>
            <CardContent>
              <ContractsTable />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="analytics">
          <ContractAnalyticsDashboard />
        </TabsContent>
        <TabsContent value="templates">
          <ContractTemplateManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
